import React, { useCallback, useRef, useEffect, useState } from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';

import Notiflix from 'notiflix';
import Input from '../../components/Input';
import getValidationErrors from '../../utils/getValidationErrors';

import {
  Container,
  Content,
  Title,
  FormContainer,
  ViewCardButton,
  FooterText,
  AppLoginLink,
  LoadingSpinner,
} from './styles';
import cardSharingService from '../../services/cardSharingService';
import { useOnlineMode } from '../../hooks/useOnlineMode';

export interface ExternalCardSharingInfo {
  cpf: string;
  token: string;
  fetchedIn: number;
}

const VirtualCard: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const history = useHistory();
  const { isOnline } = useOnlineMode();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const walletData = localStorage.getItem(
      '@unimedblumenalexternalcardsharing',
    );
    if (!isOnline && walletData) {
      history.push('/cartao-virual-externo/compartilhado');
    }
  }, [isOnline, history]);

  const handleFormSubmit = useCallback(
    async (data) => {
      if (isLoading) return;

      try {
        setIsLoading(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          cpf: Yup.string()
            .required('Digite o CPF')
            .min(11, 'CPF deve ter 11 dígitos'),
          code: Yup.string()
            .required('Digite o código de compartilhamento')
            .min(1, 'Código é obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const response = await cardSharingService.getExternalAccessWallets(
          data.cpf,
          data.code,
        );

        if (!response) {
          Notiflix.Notify.failure('Compartilhamento não encontrado');
          return;
        }

        localStorage.setItem(
          '@unimedblumenalexternalcardsharing',
          JSON.stringify(response),
        );

        localStorage.setItem(
          '@unimedblumenalexternalcardsharinginfo',
          JSON.stringify({
            fetchedIn: Date.now(),
            cpf: data.cpf,
            token: data.code,
          } as ExternalCardSharingInfo),
        );

        history.push('/cartao-virual-externo/compartilhado');
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [history, isLoading],
  );

  return (
    <Container>
      <Content>
        <Title>
          Por favor, digite seu <strong>CPF</strong> e o{' '}
          <strong>código de compartilhamento</strong>
          <br /> para exibir o cartão compartilhado com você.
        </Title>

        <FormContainer>
          <Form ref={formRef} onSubmit={handleFormSubmit}>
            <div>
              <label>CPF</label>
              <Input
                name="cpf"
                placeholder="Digite o CPF"
                masks={['999.999.999-99']}
              />
            </div>

            <div>
              <label>Código de compartilhamento</label>
              <Input
                name="code"
                placeholder="Digite o código de compartilhamento"
              />
            </div>

            <ViewCardButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <LoadingSpinner />
                  Carregando...
                </>
              ) : (
                'Ver cartão'
              )}
            </ViewCardButton>
          </Form>
        </FormContainer>

        <FooterText>
          Caso você seja beneficiário da Unimed, faça o{' '}
          <AppLoginLink to="/login">login no portal</AppLoginLink>
        </FooterText>
      </Content>
    </Container>
  );
};

export default VirtualCard;
